import { config } from './config';
import { tokenStore } from './tokenStore';

// Minimal fetch wrapper that mirrors flutter_client's DioInterceptor:
//  - attaches Authorization: Bearer <access> on protected calls
//  - on 401, refreshes via /identity/tokens/{refreshToken}/refresh once, retries
//  - strips auth for the known no-auth paths
// Pricing/places calls target a different host (cacheBaseUrl) and are unauthenticated.

export class ApiError extends Error {
  status: number;
  // Backend wraps domain errors as { Data: "error_code" } / { data: ... }.
  code?: string;
  body?: unknown;

  constructor(status: number, message: string, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

// Paths that must never carry an Authorization header (see dio_interceptor.dart).
const NO_AUTH = ['/sign', '/confirm', '/refresh', '/telegram-sign'];

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  baseUrl?: string;
  auth?: boolean;
  signal?: AbortSignal;
};

function isNoAuth(path: string): boolean {
  return NO_AUTH.some((p) => path.includes(p));
}

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  // Collapse concurrent refreshes into one in-flight request.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refresh = tokenStore.getRefresh();
    if (!refresh) return false;
    try {
      const res = await fetch(`${config.apiBaseUrl}/identity/tokens/${refresh}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      if (!res.ok) return false;
      const data = await res.json();
      const access = data.accessToken as string | undefined;
      const newRefresh = data.refreshToken as string | undefined;
      if (access && newRefresh) {
        tokenStore.setTokens(access, newRefresh);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractCode(body: unknown): string | undefined {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const raw = b.Data ?? b.data ?? b.code ?? b.error;
    if (typeof raw === 'string') return raw;
    // ASP.NET [ApiController] automatic 400 → ValidationProblemDetails:
    // { title, status, errors: { field: [messages] } }. Surface the per-field
    // errors so the rider (and logs) see exactly which field failed binding,
    // instead of a bare "(400)".
    const errors = b.errors;
    if (errors && typeof errors === 'object') {
      const parts: string[] = [];
      for (const [field, msgs] of Object.entries(errors as Record<string, unknown>)) {
        const m = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
        parts.push(`${field}: ${m}`);
      }
      if (parts.length) return parts.join(' | ');
    }
    if (typeof b.title === 'string' && b.title) return b.title;
  }
  return undefined;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, baseUrl = config.apiBaseUrl, auth = true, signal } = opts;

  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (auth && !isNoAuth(path)) {
      const access = tokenStore.getAccess();
      if (access) headers.Authorization = `Bearer ${access}`;
    }
    return headers;
  };

  const doFetch = () =>
    fetch(`${baseUrl}${path}`, {
      method,
      headers: buildHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });

  let res = await doFetch();

  // Single transparent refresh+retry on 401 for authenticated main-API calls.
  if (res.status === 401 && auth && !isNoAuth(path)) {
    const ok = await refreshTokens();
    if (ok) {
      res = await doFetch();
    } else {
      tokenStore.clear();
    }
  }

  const parsed = await parseBody(res);

  if (!res.ok) {
    // Full response body for DevTools — ValidationProblemDetails carries the
    // failing field names in `errors`, which the bare status doesn't reveal.
    console.error(`[api] ${method} ${path} -> ${res.status}`, parsed);
    throw new ApiError(
      res.status,
      `${method} ${path} failed (${res.status})`,
      extractCode(parsed),
      parsed,
    );
  }

  return parsed as T;
}
